import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";

export const usePopoverHover = ({
  delay = 50,
  margin = 5,
  isFixed = false,
}: {
  delay?: number;
  margin?: number;
  isFixed?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [arrowPosition, setArrowPosition] = useState<string>("50%");
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const anchorEl = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    if (!isFixed) {
      timeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, delay);
    }
  };
  // Calcula a posição da seta para apontar para o centro do trigger
  const calculateArrowPosition = useCallback(() => {
    if (!anchorEl.current || !popoverRef.current) {
      return;
    }

    const triggerRect = anchorEl.current.getBoundingClientRect();
    const popoverRect = popoverRef.current.getBoundingClientRect();

    if (!triggerRect || !popoverRect) {
      return;
    }

    // Calcula o centro do trigger em relação ao popover
    const triggerCenter = triggerRect.left + triggerRect.width / 2;
    const popoverLeft = popoverRect.left;

    // Posição da seta em pixels relativos ao popover
    const arrowLeft = triggerCenter - popoverLeft;

    // Limita a seta dentro dos limites do popover
    const arrowWidth = 16;
    const minOffset = arrowWidth / 2;
    const maxOffset = popoverRect.width - arrowWidth / 2;
    const clampedArrowOffset = Math.max(
      minOffset,
      Math.min(maxOffset, arrowLeft)
    );

    const newArrowPosition = `${clampedArrowOffset}px`;

    if (arrowPosition !== newArrowPosition) {
      setArrowPosition(newArrowPosition);
    }
  }, [arrowPosition]);

  useLayoutEffect(() => {
    if (isOpen && anchorEl.current) {
      const timeoutId = setTimeout(() => {
        calculateArrowPosition();

        setTimeout(calculateArrowPosition, 50);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [isOpen, calculateArrowPosition]);

  // Observer otimizado para mudanças de tamanho
  useEffect(() => {
    if (isOpen && popoverRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        calculateArrowPosition();
      });

      resizeObserver.observe(popoverRef.current);
      return () => resizeObserver.disconnect();
    }
    return undefined;
  }, [isOpen, calculateArrowPosition]);
  // Função otimizada para verificar se o mouse está sobre o componente
  const isMouseOverComponent = useCallback(
    (e: MouseEvent) => {
      const anchorElement = anchorEl.current;
      const popoverElement = popoverRef.current;

      if (!anchorElement) return false;

      // Verifica trigger com margem
      const anchorRect = anchorElement.getBoundingClientRect();
      const isOverAnchor =
        e.clientX >= anchorRect.left - margin &&
        e.clientX <= anchorRect.right + margin &&
        e.clientY >= anchorRect.top - margin &&
        e.clientY <= anchorRect.bottom + margin;

      if (isOverAnchor) return true;

      // Verifica popover se estiver visível
      if (popoverElement) {
        const popoverRect = popoverElement.getBoundingClientRect();
        return (
          e.clientX >= popoverRect.left - margin &&
          e.clientX <= popoverRect.right + margin &&
          e.clientY >= popoverRect.top - margin &&
          e.clientY <= popoverRect.bottom + margin
        );
      }

      return false;
    },
    [margin]
  );

  // Event listeners otimizados
  useEffect(() => {
    if (!isOpen) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isFixed && !isMouseOverComponent(e)) {
        handleMouseLeave();
      }
    };

    const handleResize = () => calculateArrowPosition();

    // Aplica estilos apenas uma vez e somente se houver scroll
    const htmlElement = document.querySelector("html");
    const headerElement = document.querySelector(
      "#page-header div"
    ) as HTMLElement;

    // Verifica se a página tem scroll vertical
    const hasVerticalScroll =
      document.documentElement.scrollHeight > window.innerHeight;

    if (hasVerticalScroll && htmlElement) {
      htmlElement.style.overflow = "hidden";
      htmlElement.style.paddingRight = "0.5rem";
    }
    if (hasVerticalScroll && headerElement) {
      headerElement.style.paddingRight = "2.5rem";
    }

    // Adiciona listeners apenas se necessário
    if (!isFixed) {
      document.addEventListener("mousemove", handleGlobalMouseMove, {
        passive: true,
      });
    }
    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      // Cleanup - só reverte se foi aplicado (ou seja, se havia scroll)
      const hasVerticalScroll =
        document.documentElement.scrollHeight > window.innerHeight;

      if (hasVerticalScroll && htmlElement) {
        htmlElement.style.overflow = "auto";
        htmlElement.style.paddingRight = "";
      }
      if (hasVerticalScroll && headerElement) {
        headerElement.style.paddingRight = "";
      }
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      window.removeEventListener("resize", handleResize);
    };
  }, [
    isOpen,
    isFixed,
    isMouseOverComponent,
    calculateArrowPosition,
    handleMouseLeave,
  ]);

  // Cleanup do timeout quando o componente é desmontado
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isOpen,
    arrowPosition,
    popoverPosition: null,
    anchorEl,
    popoverRef,
    handleMouseEnter,
    handleMouseLeave,
  };
};
